import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { CRM } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, MagnifyingGlass, PencilSimple, Trash, Stethoscope, MapPin, CurrencyInr } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

export default function CustomersPage() {
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.getDoctors(search, areaFilter); setDoctors(data); } catch {}
    setLoading(false);
  }, [search, areaFilter]);

  useEffect(() => { load(); }, [load]);

  const areas = [...new Set(doctors.map(d => d.area).filter(Boolean))];

  return (
    <div data-testid={CRM.container} className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Doctor CRM</h1>
          <p className="text-sm mt-1" style={{ color: '#8A94A6' }}>{doctors.length} doctors - MR field calls management</p>
        </div>
        <Button data-testid={CRM.addCustomerBtn} onClick={() => { setEditDoc(null); setShowForm(true); }}
          className="h-9 px-4 text-sm font-semibold rounded-md" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={16} weight="bold" className="mr-1" /> Add Doctor
        </Button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
          <Input data-testid={CRM.searchInput} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search doctors by name, specialization..." className="pl-10 h-9" />
        </div>
        <Select value={areaFilter} onValueChange={v => setAreaFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40 h-9"><SelectValue placeholder="All Areas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Areas</SelectItem>
            {areas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Doctor Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <p className="col-span-3 text-center py-8" style={{ color: '#8A94A6' }}>Loading...</p>
        ) : doctors.map(d => (
          <div key={d.id} className="p-4 rounded-md border hover:shadow-sm transition-shadow" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#007AFF10' }}>
                  <Stethoscope size={20} weight="duotone" style={{ color: '#007AFF' }} />
                </div>
                <div>
                  <p className="text-sm font-bold" style={{ color: '#0A2540' }}>{d.name}</p>
                  <p className="text-xs" style={{ color: '#8A94A6' }}>{d.specialization}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditDoc(d); setShowForm(true); }} className="p-1 rounded hover:bg-slate-100"><PencilSimple size={14} style={{ color: '#007AFF' }} /></button>
                <button onClick={async () => { if (window.confirm('Delete?')) { await api.deleteDoctor(d.id); load(); } }} className="p-1 rounded hover:bg-red-50"><Trash size={14} style={{ color: '#FF3B30' }} /></button>
              </div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs" style={{ color: '#8A94A6' }}>
                <MapPin size={12} /> {d.clinic_address || 'No address'}
              </div>
              <div className="flex items-center gap-1.5 text-xs" style={{ color: '#8A94A6' }}>
                Area: <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: '#F0F4F8', color: '#0A2540' }}>{d.area || '-'}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs" style={{ color: '#8A94A6' }}>
                <CurrencyInr size={12} /> Commission: <span className="font-semibold" style={{ color: '#34C759' }}>{d.commission_percent}%</span>
              </div>
            </div>
            <div className="mt-3 pt-3 flex gap-2" style={{ borderTop: '1px solid #E2E4E9' }}>
              <button onClick={() => navigate('/admin/doctor-panel')} className="text-[10px] font-semibold px-2 py-1 rounded" style={{ background: '#007AFF10', color: '#007AFF' }}>
                View Earnings
              </button>
              <span className="text-[10px] px-2 py-1 rounded" style={{ background: '#F0F4F8', color: '#8A94A6' }}>
                Ph: {d.phone || '-'}
              </span>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editDoc ? 'Edit Doctor' : 'Add Doctor'}</DialogTitle></DialogHeader>
          <DoctorForm doctor={editDoc} onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DoctorForm({ doctor, onSave }) {
  const [form, setForm] = useState(doctor || { name: '', specialization: '', phone: '', clinic_address: '', area: '', latitude: null, longitude: null, commission_percent: 0 });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      if (doctor?.id) await api.updateDoctor(doctor.id, form);
      else await api.createDoctor(form);
      onSave();
    } catch {}
  };
  return (
    <div className="space-y-3">
      {[
        { k: 'name', label: 'Doctor Name' }, { k: 'specialization', label: 'Specialization' },
        { k: 'phone', label: 'Phone' }, { k: 'clinic_address', label: 'Clinic Address' },
        { k: 'area', label: 'Area / Beat' },
      ].map(f => (
        <div key={f.k}>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{f.label}</Label>
          <Input value={form[f.k] || ''} onChange={e => update(f.k, e.target.value)} className="h-8 mt-1 text-sm" />
        </div>
      ))}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Latitude</Label>
          <Input type="number" step="any" value={form.latitude || ''} onChange={e => update('latitude', Number(e.target.value) || null)} className="h-8 mt-1 text-sm" />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Longitude</Label>
          <Input type="number" step="any" value={form.longitude || ''} onChange={e => update('longitude', Number(e.target.value) || null)} className="h-8 mt-1 text-sm" />
        </div>
      </div>
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Commission %</Label>
        <Input type="number" value={form.commission_percent} onChange={e => update('commission_percent', Number(e.target.value))} className="h-8 mt-1 text-sm" />
      </div>
      <Button onClick={save} className="w-full h-9 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>Save Doctor</Button>
    </div>
  );
}
